import { Nullish } from '@enluma/common';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CopyResult } from './types';
import { UserProfile } from '../models';

export {
  isDefined,
  isNotDefined,
  isObjectEmpty,
  isObjectNotEmpty,
  isError,
  delay,
  createFullName,
} from '@enluma/common';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const copyToClipboard = async (text: string): Promise<CopyResult> => {

  // Note: clipboard APIs often require a user gesture (click/tap) and secure context (HTTPS).
  try {
    await navigator.clipboard.writeText(text);
    return 'success';
  } catch (err) {
    // navigator.clipboard may fail on some iOS Safari scenarios (no gesture/permission)
    // fall through to legacy fallback below
    console.warn(
      'navigator.clipboard.writeText failed, falling back to other methods',
      err,
    );
  }

  try {
    // Try the more modern Clipboard.write API (supports rich types and may be available
    // where writeText isn't). This avoids using the deprecated execCommand.
    const blob = new Blob([text], { type: 'text/plain' });
    const item = new ClipboardItem({ 'text/plain': blob });
    await navigator.clipboard.write([item]);
    return 'success';
  } catch (err) {
    console.warn(
      'navigator.clipboard.write failed, falling back to prompt',
      err,
    );
  }

  // Legacy fallback: try the deprecated document.execCommand('copy') approach using a hidden textarea.
  try {
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      // Prevent scrolling to bottom on iOS and keep element out of view
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.width = '1px';
      textarea.style.height = '1px';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      textarea.readOnly = true;

      document.body.appendChild(textarea);
      textarea.select();
      try {
        textarea.setSelectionRange(0, textarea.value.length);
      } catch (_e) {
        // setSelectionRange may fail in some browsers, ignore
      }

      let successful = false;
      try {
        // document.execCommand is deprecated, but still works in many older browsers
        // queryCommandSupported is optional; if unavailable, fall back to execCommand call directly
        successful =
          document.queryCommandSupported &&
          document.queryCommandSupported('copy')
            ? document.execCommand('copy')
            : document.execCommand('copy');
      } catch (_err) {
        // ignore
      }

      document.body.removeChild(textarea);

      if (successful) {
        return 'success';
      }
    }
  } catch (err) {
    console.warn('execCommand copy failed, falling back to prompt', err);
  }

  // Last-resort fallback: show a prompt so users (especially on iOS Safari) can copy manually.
  try {
    if (typeof window !== 'undefined' && typeof window.prompt === 'function') {
      // The prompt displays the text so the user can long-press (mobile) or Cmd/Ctrl+C (desktop)
      // and copy it manually. Returning 'prompt' indicates we provided a usable fallback
      // which requires the user to copy themselves.
      window.prompt('Copy to clipboard: Select and copy', text);
      return 'prompt';
    }
  } catch (error) {
    console.error('Prompt fallback failed', error);
  }

  return 'failed';
};

export const needToShowOnboarding = (user: Nullish<UserProfile>) => {
  // Show onboarding for any user with a doctor profile that's incomplete
  // (doctors and self-registered doctor-admins)
  if (!user?.doctorProfile) {
    return false;
  }

  const hasNotUsername = !Boolean(user?.doctorProfile?.username);
  const hasNotSpeciality = !Boolean(
    user?.doctorProfile?.speciality && user?.doctorProfile?.subSpeciality,
  );

  if (hasNotUsername) {
    return true;
  } else if (hasNotSpeciality) {
    return true;
  }

  return false;
};

export function getFrontendUrl(defaultUrl = 'https://enlumaconsult.health') {
  const url = import.meta.env.VITE_FRONTEND_URL ?? defaultUrl;
  return String(url).replace(/\/$/, '');
}
